import { AgGridTable } from 'components/AgGrid/AgGridTable';
import { getRowId } from 'components/AgGrid/utils';
import type { IExternalFormMultiselectItem } from 'components/ExternalFormMultiselect';
import { ExternalFormMultiselect } from 'components/ExternalFormMultiselect';
import { ModalDialog } from 'components/ModalDialog';
import type { RatioRowData } from 'gql/graphql';
import { RatioTypeEnum } from 'gql/graphql';
import { AddCoefficientTabsTypes } from 'pages/Project/UerProject/AddCoefficientTabsTypes';
import { cnMixSpace, HeaderSearchBar, Select } from 'uikit';

import type { RowClassRules } from 'ag-grid-community';
import type { AgGridReact } from 'ag-grid-react';
import { debounce } from 'debounce';
import type React from 'react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { useUerCoefficientsQuoteColumns } from './hooks/useUerCoefficientsQuoteColumns';
import { useUerRatioCoefficientsQuoteData } from './hooks/useUerRatioCoefficientsQuoteData';

import Styles from './uerCoefficientsQuoteStyle.module.css';

export interface IRatioItems {
    rationId: number;
    name: string;
    code: string;
}

export interface IRatioGroups {
    groupId: number;
    name: string;
}

export interface IRatioDismantling {
    currentId: number;
    dataGroup: boolean;
    name: string;
    subGroupId: number;
}

export interface IValueSelect {
    label: string;
    id: number;
    subGroupId: boolean;
}

export interface IFilterTable {
    [key: string]: string[];
}

interface IUerRatioSelectorProps {
    code: string;
    label?: string;
    ratioItems: IRatioItems[];
    ratioGroups: IRatioGroups[];
    ratioDem: IRatioDismantling[];
    onChange?: (
        ratioIds: number[],
        ratioGroupIds: number[],
        ratioGroupIdsDem: number[],
        selectedRatios: IExternalFormMultiselectItem[],
        selectedRatioGroups: IExternalFormMultiselectItem[],
        selectedRatioGroupsDem: IExternalFormMultiselectItem[],
    ) => void;
    refCheckBtn?: React.MutableRefObject<null>;
    disabled?: boolean;
    disabledIds: number[];
}

const getExternalSelectItemByRatioItem = (v: IRatioItems): IExternalFormMultiselectItem => ({
    id: v.rationId,
    label: v.code,
    data: v,
});
const getExternalSelectItemByRatioGroupItem = (v: IRatioGroups): IExternalFormMultiselectItem => ({
    id: v.groupId,
    label: v.name,
    data: v,
});

const getExternalSelectItemByRatioGroupOrItem = (v: any): IExternalFormMultiselectItem => ({
    id: v.currentId,
    label: v.name,
    data: v,
});

const getExternalSelectItems = (
    ratioItems: IRatioItems[],
    ratioGroups: IRatioGroups[],
    ratioDem: IRatioDismantling[],
) => ({
    [RatioTypeEnum.ConstraintCoef]: ratioGroups.map(getExternalSelectItemByRatioGroupItem),
    [RatioTypeEnum.TechPartCoef]: ratioItems.map(getExternalSelectItemByRatioItem),
    [RatioTypeEnum.DismantlingCoef]: ratioDem.map(getExternalSelectItemByRatioGroupOrItem),
});

const defaultRates = {
    [RatioTypeEnum.ConstraintCoef]: [] as IExternalFormMultiselectItem[],
    [RatioTypeEnum.TechPartCoef]: [] as IExternalFormMultiselectItem[],
    [RatioTypeEnum.DismantlingCoef]: [] as IExternalFormMultiselectItem[],
};

export const defaultColDef = {
    resizable: true,
    wrapHeaderText: true,
    spanHeaderHeight: true,
};

export const getRowClass: RowClassRules = {
    groupRow: ({ data }: { data: { groupRow: boolean } }) => data?.groupRow,
};
interface IValueTab {
    value: IValueSelect | undefined;
}

export const UerCoefficientsQuote: React.FC<IUerRatioSelectorProps> = (props) => {
    const { label, refCheckBtn, onChange, ratioItems, ratioGroups, ratioDem, disabled, disabledIds } = props;
    const [isSearchOpen, setIsSearchOpen] = useState(false);
    const gridRef = useRef<AgGridReact<RatioRowData>>(null);

    const [tabValues, setTabValues] = useState<{
        firstTab: IValueTab;
        thirdTab: IValueTab;
    }>({
        firstTab: { value: undefined },
        thirdTab: { value: undefined },
    });

    const [searchValueAll, setSearchValueAll] = useState({
        [RatioTypeEnum.ConstraintCoef]: '',
        [RatioTypeEnum.TechPartCoef]: '',
        [RatioTypeEnum.DismantlingCoef]: '',
    });

    const [debounceValue, setDebounceValue] = useState({
        [RatioTypeEnum.ConstraintCoef]: '',
        [RatioTypeEnum.TechPartCoef]: '',
        [RatioTypeEnum.DismantlingCoef]: '',
    });

    const [activeTabId, setActiveTabId] = useState<RatioTypeEnum>(RatioTypeEnum.ConstraintCoef);
    const [checkedRates, setCheckedRates] = useState(defaultRates);
    const [formCheckedRates, setFormCheckedRates] = useState(defaultRates);
    const [filters, setFilters] = useState({
        [RatioTypeEnum.ConstraintCoef]: {},
        [RatioTypeEnum.TechPartCoef]: {},
        [RatioTypeEnum.DismantlingCoef]: {},
    });

    const formCheckedItemsCount: { [key: string]: number } = useMemo(
        () => ({
            [RatioTypeEnum.ConstraintCoef]: formCheckedRates[RatioTypeEnum.ConstraintCoef].length,
            [RatioTypeEnum.TechPartCoef]: formCheckedRates[RatioTypeEnum.TechPartCoef].length,
            [RatioTypeEnum.DismantlingCoef]: formCheckedRates[RatioTypeEnum.DismantlingCoef].length,
        }),
        [formCheckedRates],
    );

    const currentValueSearch = useMemo(() => searchValueAll[activeTabId], [searchValueAll, activeTabId]);
    const currentDebounceValue = useMemo(() => debounceValue[activeTabId], [debounceValue, activeTabId]);

    useEffect(() => {
        setCheckedRates(getExternalSelectItems(ratioItems, ratioGroups, ratioDem));
    }, [ratioDem, ratioGroups, ratioItems]);

    const onSearchOpen = useCallback(() => {
        setFormCheckedRates({
            [RatioTypeEnum.ConstraintCoef]: [...checkedRates[RatioTypeEnum.ConstraintCoef]],
            [RatioTypeEnum.TechPartCoef]: [...checkedRates[RatioTypeEnum.TechPartCoef]],
            [RatioTypeEnum.DismantlingCoef]: [...checkedRates[RatioTypeEnum.DismantlingCoef]],
        });
        setIsSearchOpen(true);
    }, [checkedRates]);

    const onSearchClose = useCallback(() => {
        setFormCheckedRates({
            [RatioTypeEnum.ConstraintCoef]: [],
            [RatioTypeEnum.TechPartCoef]: [],
            [RatioTypeEnum.DismantlingCoef]: [],
        });
        setActiveTabId(RatioTypeEnum.ConstraintCoef);
        setIsSearchOpen(false);
    }, []);

    const onSearchFormSubmit = useCallback(() => {
        const newRates = {
            [RatioTypeEnum.ConstraintCoef]: [...formCheckedRates[RatioTypeEnum.ConstraintCoef]],
            [RatioTypeEnum.TechPartCoef]: [...formCheckedRates[RatioTypeEnum.TechPartCoef]],
            [RatioTypeEnum.DismantlingCoef]: [...formCheckedRates[RatioTypeEnum.DismantlingCoef]],
        };

        setCheckedRates(newRates);
        setFormCheckedRates({
            [RatioTypeEnum.ConstraintCoef]: [],
            [RatioTypeEnum.TechPartCoef]: [],
            [RatioTypeEnum.DismantlingCoef]: [],
        });
        onSearchClose();

        onChange?.(
            newRates[RatioTypeEnum.TechPartCoef].map((i: IExternalFormMultiselectItem) => i.id),
            [...newRates[RatioTypeEnum.ConstraintCoef].map((i: IExternalFormMultiselectItem) => i.id)],
            [...newRates[RatioTypeEnum.DismantlingCoef].map((i: IExternalFormMultiselectItem) => i.id)],
            newRates[RatioTypeEnum.TechPartCoef],
            [...newRates[RatioTypeEnum.ConstraintCoef]],
            [...newRates[RatioTypeEnum.DismantlingCoef]],
        );
    }, [formCheckedRates, onChange, onSearchClose]);

    const onSearch = useCallback(
        (value) => {
            setDebounceValue((prev) => ({
                ...prev,
                [activeTabId]: value,
            }));
        },
        [activeTabId],
    );

    const debouncedSearch = debounce(onSearch, 500);

    const onSearchValueChange = useCallback(
        ({ value }) => {
            setSearchValueAll((prev) => ({
                ...prev,
                [activeTabId]: value,
            }));
            debouncedSearch(value);
        },
        [activeTabId, debouncedSearch],
    );

    const onMultiselectChange = useCallback(
        (selectedItems: IExternalFormMultiselectItem[]) => {
            const selectedItemsIds = selectedItems?.map((i) => i.id);

            const newRates = {
                [RatioTypeEnum.ConstraintCoef]: checkedRates[RatioTypeEnum.ConstraintCoef].filter(
                    (c: IExternalFormMultiselectItem) => selectedItemsIds?.includes(c.id),
                ),
                [RatioTypeEnum.TechPartCoef]: checkedRates[RatioTypeEnum.TechPartCoef].filter(
                    (c: IExternalFormMultiselectItem) => selectedItemsIds?.includes(c.id),
                ),
                [RatioTypeEnum.DismantlingCoef]: checkedRates[RatioTypeEnum.DismantlingCoef].filter(
                    (c: IExternalFormMultiselectItem) => selectedItemsIds?.includes(c.id),
                ),
            };

            setCheckedRates(newRates);

            onChange?.(
                newRates[RatioTypeEnum.TechPartCoef].map((i: IExternalFormMultiselectItem) => i.id),
                [...newRates[RatioTypeEnum.ConstraintCoef].map((i: IExternalFormMultiselectItem) => i.id)],
                [...newRates[RatioTypeEnum.DismantlingCoef].map((i: IExternalFormMultiselectItem) => i.id)],
                newRates[RatioTypeEnum.TechPartCoef],
                [...newRates[RatioTypeEnum.ConstraintCoef]],
                [...newRates[RatioTypeEnum.DismantlingCoef]],
            );
        },
        [checkedRates, onChange],
    );

    const selectedItems = useMemo(
        () => [
            ...checkedRates[RatioTypeEnum.ConstraintCoef],
            ...checkedRates[RatioTypeEnum.TechPartCoef],
            ...checkedRates[RatioTypeEnum.DismantlingCoef],
        ],
        [checkedRates],
    );
    const formSelectedItems = useMemo(
        () => [
            ...formCheckedRates[RatioTypeEnum.ConstraintCoef],
            ...formCheckedRates[RatioTypeEnum.TechPartCoef],
            ...formCheckedRates[RatioTypeEnum.DismantlingCoef],
        ],
        [formCheckedRates],
    );
    const formCheckedIds = useMemo(
        () => formSelectedItems.map((s: IExternalFormMultiselectItem) => s.id),
        [formSelectedItems],
    );

    const currentValueSelect = useMemo(() => {
        if (activeTabId === RatioTypeEnum.ConstraintCoef) {
            return tabValues.firstTab.value;
        }

        if (activeTabId === RatioTypeEnum.DismantlingCoef) {
            return tabValues.thirdTab.value;
        }

        return undefined;
    }, [activeTabId, tabValues.firstTab.value, tabValues.thirdTab.value]);

    const onRowCheckedChange = useCallback(
        (item: IExternalFormMultiselectItem, isChecked: boolean) => {
            const currentNewDataItem =
                activeTabId === RatioTypeEnum.ConstraintCoef ||
                (activeTabId === RatioTypeEnum.DismantlingCoef && !currentValueSelect?.subGroupId)
                    ? [item]
                    : [...formCheckedRates[activeTabId], item];

            const resultTabData =
                activeTabId === RatioTypeEnum.DismantlingCoef && !!currentValueSelect?.subGroupId
                    ? currentNewDataItem.filter((i) => i.data.subGroupId !== undefined)
                    : currentNewDataItem;

            setFormCheckedRates({
                ...formCheckedRates,
                [activeTabId]: isChecked
                    ? resultTabData
                    : formCheckedRates[activeTabId].filter((r: IExternalFormMultiselectItem) => r?.id !== item.id),
            });
        },
        [activeTabId, currentValueSelect?.subGroupId, formCheckedRates],
    );

    const {
        contractIdObj,
        datasource,
        hasCurrentData,
        selectItems,
        selectedItemsData,
        dataItems,
        isWorkConditionsHasSubGroupId,
    } = useUerRatioCoefficientsQuoteData({
        ratioType: activeTabId,
        currentDebounceValue,
        filters,
        currentValueSelect,
        gridRef,
    });

    const changeFilterHandler = useCallback<(params: { fieldName: string; checkedValues: string[] }) => void>(
        ({ fieldName, checkedValues }) => {
            setFilters((prevValue) => ({
                ...prevValue,
                [activeTabId]: {
                    ...prevValue[activeTabId],
                    [fieldName]: checkedValues,
                },
            }));
        },
        [activeTabId],
    );

    const [columns] = useUerCoefficientsQuoteColumns({
        onRowCheckedChange,
        checkedIds: formCheckedIds,
        ratioType: activeTabId,
        contractId: contractIdObj.contractId,
        changeFilterHandler,
        hasSubGroupId: selectedItemsData[0]?.subGroupId,
        isWorkConditionsHasSubGroupId,
    });

    const handleOnChange = useCallback(
        ({ value: selectedValue }) => {
            if (!selectedValue) {
                return;
            }
            setTabValues((prev) => {
                if (activeTabId === RatioTypeEnum.ConstraintCoef) {
                    return {
                        ...prev,
                        firstTab: {
                            ...prev.firstTab,
                            value: selectedValue,
                        },
                    };
                }

                if (activeTabId === RatioTypeEnum.DismantlingCoef) {
                    return {
                        ...prev,
                        thirdTab: {
                            ...prev.thirdTab,
                            value: selectedValue,
                        },
                    };
                }

                return prev;
            });
        },
        [activeTabId],
    );

    const gridOptions = {
        rowHeight: 40,
    };

    const disabledBar =
        undefined === currentValueSelect?.id && activeTabId !== RatioTypeEnum.TechPartCoef ? Styles.DisabledBar : '';
    const foolBar = activeTabId === RatioTypeEnum.TechPartCoef ? Styles.FoolBar : '';
    const searchBarClassName = `${Styles.UserRatioSearch} ${disabledBar} ${foolBar}`;

    const currentDatasource = useMemo(
        () => (activeTabId === RatioTypeEnum.TechPartCoef ? datasource : undefined),
        [activeTabId, datasource],
    );

    const rowData = useMemo(
        () => (activeTabId !== RatioTypeEnum.TechPartCoef ? dataItems : undefined),
        [activeTabId, dataItems],
    );

    const rowModelType = useMemo(
        () => (activeTabId === RatioTypeEnum.TechPartCoef ? 'infinite' : undefined),
        [activeTabId],
    );

    const showTable = useMemo(
        () => activeTabId === RatioTypeEnum.TechPartCoef || !!currentValueSelect,
        [activeTabId, currentValueSelect],
    );

    return (
        <ExternalFormMultiselect
            disabled={disabled}
            refCheckBtn={refCheckBtn}
            label={label}
            labelPosition="left"
            onChange={onMultiselectChange}
            onSearchFormOpen={onSearchOpen}
            selectedItems={selectedItems}
            disabledIds={disabledIds}
            countTitleType="коэффициентов"
        >
            <ModalDialog
                isOpen={isSearchOpen}
                title="Добавление коэффициента к расценке"
                onConfirm={onSearchFormSubmit}
                onClose={onSearchClose}
                withTitleCloseButton
                btnConfirmLabel="Добавить к расценке"
                className={Styles.uerRatioSelectorDialog}
            >
                <AddCoefficientTabsTypes
                    onChangeActiveTab={setActiveTabId}
                    countItems={formCheckedItemsCount}
                    className={cnMixSpace({ p: 'xl' })}
                    view="bordered"
                />
                <div className={Styles.content}>
                    <div className={Styles.wrappControl}>
                        {activeTabId !== RatioTypeEnum.TechPartCoef && (
                            <Select
                                className={Styles.selectTypeConstruction}
                                items={selectItems}
                                value={currentValueSelect}
                                onChange={handleOnChange}
                                labelPosition="left"
                                placeholder="Вид работ"
                                getItemLabel={(i: IValueSelect) => i.label || ''}
                                getItemKey={(i) => i.id}
                                defaultValue={undefined}
                            />
                        )}
                        <HeaderSearchBar
                            className={searchBarClassName}
                            placeholder="Поиск коэффициента в прейскуранте"
                            label="поиск"
                            value={currentValueSearch}
                            onChange={onSearchValueChange}
                        />
                    </div>
                    {!currentValueSelect && activeTabId !== RatioTypeEnum.TechPartCoef && (
                        <div className={Styles.overlayNoRowsTemplate}>
                            <p>Для отображения данных выберите вид работ</p>
                        </div>
                    )}
                    {showTable && (
                        <div className={`${Styles.tableMainWrapper} ag-theme-alpine`}>
                            <AgGridTable
                                className={Styles.UerCoefficientsTable}
                                getRowId={getRowId}
                                gridOptions={gridOptions}
                                rowData={rowData}
                                datasource={currentDatasource}
                                rowModelType={rowModelType}
                                maxConcurrentDatasourceRequests={1}
                                maxBlocksInCache={50}
                                cacheBlockSize={50}
                                columnDefs={columns}
                                defaultColDef={defaultColDef}
                                rowClassRules={getRowClass}
                                suppressCellFocus
                                ref={gridRef}
                                key={activeTabId}
                            />
                            {!hasCurrentData && (
                                <div className={Styles.customOverlay}>
                                    {activeTabId === RatioTypeEnum.TechPartCoef ? 'Нет данных' : ''}
                                </div>
                            )}
                        </div>
                    )}
                </div>
            </ModalDialog>
        </ExternalFormMultiselect>
    );
};

UerCoefficientsQuote.displayName = 'UerCoefficientsQuote';
